import React from 'react';
import { Area, Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Globe2, MonitorSmartphone, TrendingUp } from 'lucide-react';

const PALETTE = ['#7B4FD8', '#F5831F', '#2BA84A', '#2D9CDB', '#E04F77', '#8A7FA3'];

const fmtDay = (d) => {
  const dt = new Date(d + 'T00:00:00Z');
  return `${dt.getUTCDate()} ${dt.toLocaleString('en', { month: 'short', timeZone: 'UTC' })}`;
};

const BarList = ({ title, items, testid }) => {
  const max = Math.max(1, ...(items || []).map((i) => i.count));
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={testid}>
      <p className="text-xs uppercase tracking-[0.14em] text-gray-500 mb-3">{title}</p>
      {(items || []).length === 0 && <p className="text-sm text-gray-400">No data yet</p>}
      <div className="space-y-2">
        {(items || []).map((i) => (
          <div key={i.name}>
            <div className="flex justify-between text-[13px] mb-0.5">
              <span className="text-gray-800 truncate pr-2" title={i.name}>{i.name}</span>
              <span className="text-gray-500 font-semibold">{i.count.toLocaleString()}</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100">
              <div className="h-1.5 rounded-full bg-[#7B4FD8]" style={{ width: `${(i.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DonutCard = ({ title, items, testid }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={testid}>
    <p className="text-xs uppercase tracking-[0.14em] text-gray-500 mb-1">{title}</p>
    {(items || []).length === 0 ? <p className="text-sm text-gray-400 mt-2">No data yet</p> : (
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={items} dataKey="count" nameKey="name" innerRadius={38} outerRadius={60} paddingAngle={2}>
              {items.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Pie>
            <Tooltip formatter={(v, n) => [v.toLocaleString(), n]} />
            <Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
);

export const TrafficAnalytics = ({ data, testidPrefix = 'traffic' }) => {
  if (!data) return null;
  const t = data.totals || {};
  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-analytics-root`}>
      {/* Headline numbers */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: `Visitors (${data.days}d)`, value: t.visitors, Icon: Globe2, id: 'visitors' },
          { label: 'Page views', value: t.pageviews, Icon: MonitorSmartphone, id: 'pageviews' },
          { label: 'New signups', value: t.signups, Icon: TrendingUp, id: 'signups' },
        ].map(({ label, value, Icon, id }) => (
          <div key={id} className="bg-white rounded-2xl border border-gray-200 p-4" data-testid={`${testidPrefix}-stat-${id}`}>
            <div className="flex items-center gap-2 text-gray-500 text-[11px] uppercase tracking-[0.12em]"><Icon size={13} /> {label}</div>
            <p className="text-2xl font-extrabold text-gray-900 mt-1">{(value ?? 0).toLocaleString()}</p>
          </div>
        ))}
      </div>

      {/* Daily trend */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`${testidPrefix}-daily-chart`}>
        <p className="text-xs uppercase tracking-[0.14em] text-gray-500 mb-3">Daily visitors, page views &amp; signups</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.daily || []} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7B4FD8" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#7B4FD8" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0EADF" />
              <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 10 }} interval="preserveStartEnd" minTickGap={28} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip labelFormatter={fmtDay} />
              <Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="pageviews" name="Page views" stroke="#7B4FD8" strokeWidth={2} fill="url(#pvGrad)" />
              <Line type="monotone" dataKey="visitors" name="Visitors" stroke="#F5831F" strokeWidth={2} dot={false} />
              <Bar dataKey="signups" name="Signups" fill="#2BA84A" radius={[3, 3, 0, 0]} barSize={10} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Breakdown grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <BarList title="Top pages" items={data.top_pages} testid={`${testidPrefix}-top-pages`} />
        <BarList title="Countries" items={data.countries} testid={`${testidPrefix}-countries`} />
        <BarList title="Referrers" items={data.referrers} testid={`${testidPrefix}-referrers`} />
        <DonutCard title="Browsers" items={data.browsers} testid={`${testidPrefix}-browsers`} />
        <DonutCard title="Devices" items={data.devices} testid={`${testidPrefix}-devices`} />
        <BarList title="Languages" items={data.languages} testid={`${testidPrefix}-languages`} />
      </div>

      <p className="text-[11px] text-gray-500" data-testid={`${testidPrefix}-generated-at`}>
        First-party, cookieless analytics counted live from hibff.com{data.generated_at ? ` — generated ${new Date(data.generated_at).toLocaleString()}` : ''}. Test and demo accounts excluded from signups.
      </p>
    </div>
  );
};

export default TrafficAnalytics;
